import { Injectable, inject } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, take, switchMap, catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { UserStatus } from '../../shared/models/user.model';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const isPlugin = state.url === '/plugin' || state.url.startsWith('/plugin/') || !!(typeof window !== 'undefined' && (window as any).angulerisPlugin);

    return this.authService.isAuthenticated$.pipe(
      take(1),
      switchMap(isAuthenticated => {
        if (!isAuthenticated) {
          const loginUrl = isPlugin ? '/plugin/login' : '/login';
          this.router.navigate([loginUrl]);
          return of(false);
        }
        // Refresh user to ensure we have the latest status
        return this.authService.refreshCurrentUser().pipe(
          map(user => {
            if (user?.mustChangePassword && state.url !== '/change-password') {
              this.router.navigate(['/change-password'], {
                queryParams: isPlugin ? { returnUrl: '/plugin/dashboard' } : undefined
              });
              return false;
            }
            if (user?.status === UserStatus.Inactive || user?.status === UserStatus.Deleted) {
              this.router.navigate(['/frozen-account']);
              return false;
            }
            return true;
          }),
          catchError(() => {
            const loginUrl = isPlugin ? '/plugin/login' : '/login';
            this.router.navigate([loginUrl]);
            return of(false);
          })
        );
      })
    );
  }
}
